import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useVenues } from './VenueContext';
import { VenueReportForm } from './VenueReportForm';
import { formatVenueSearchResultMeta, searchVenuesByQuery } from '../utils/searchUtils';
import { getResponsiveValue } from '../styles/breakpoints';

const ROLES = [
  { label: 'Artist', value: 'artist' },
  { label: 'Employee', value: 'employee' },
  { label: 'Crew', value: 'touring' },
  { label: 'Other', value: 'other' },
];

const GRADE_SEARCH_LIMIT = 20;

export function gradeVenueMatches(venues, query, { limit = GRADE_SEARCH_LIMIT } = {}) {
  const pool = Array.isArray(venues) ? venues : [];
  const needle = String(query || '').trim();
  if (!needle) return [];
  return searchVenuesByQuery(pool, needle, { limit });
}

export function GradeVenueModal({ visible, onClose }) {
  const { allVenues, venues, loading } = useVenues();
  const [q, setQ] = useState('');
  const [venue, setVenue] = useState(null);
  const [role, setRole] = useState('');
  const [step, setStep] = useState('pick');

  const catalog = allVenues.length > 0 ? allVenues : venues;
  const matches = useMemo(() => gradeVenueMatches(catalog, q), [catalog, q]);
  const canContinue = Boolean(venue && role);
  const showEmptyHint = !q.trim();
  const showNoHits = Boolean(q.trim()) && !loading && matches.length === 0;
  const showingForm = step === 'form' && Boolean(venue && role);

  const resetForm = () => {
    setQ('');
    setVenue(null);
    setRole('');
    setStep('pick');
  };

  useEffect(() => {
    if (!visible) resetForm();
  }, [visible]);

  const resetAndClose = () => {
    resetForm();
    onClose();
  };

  const sidebarWidth = showingForm
    ? getResponsiveValue({ mobile: '100%', tablet: '92%', laptop: 560, desktop: 560 })
    : getResponsiveValue({ mobile: '100%', tablet: '90%', laptop: 500, desktop: 500 });

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={resetAndClose}>
      <Pressable
        onPress={resetAndClose}
        style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.5)',
          justifyContent: 'flex-end',
          alignItems: 'flex-end',
          ...(Platform.OS === 'web' ? { cursor: 'default' } : null),
        }}
      >
        <Pressable
          onPress={(e) => e.stopPropagation?.()}
          style={{
            width: sidebarWidth,
            maxWidth: showingForm ? 560 : 500,
            height: '100%',
            backgroundColor: '#111111',
            borderLeftWidth: 1,
            borderLeftColor: 'rgba(255,255,255,0.08)',
            ...(Platform.OS === 'web'
              ? { boxShadow: '-2px 0 12px rgba(0,0,0,0.5)', cursor: 'auto' }
              : {
                  shadowColor: '#000',
                  shadowOffset: { width: -2, height: 0 },
                  shadowOpacity: 0.25,
                  shadowRadius: 10,
                  elevation: 10,
                }),
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingHorizontal: 16,
              paddingVertical: 14,
              borderBottomWidth: 1,
              borderBottomColor: 'rgba(255,255,255,0.1)',
              gap: 8,
            }}
          >
            <View style={{ flex: 1, minWidth: 0 }}>
              {showingForm ? (
                <TouchableOpacity onPress={() => setStep('pick')} style={{ alignSelf: 'flex-start', marginBottom: 4 }}>
                  <Text style={{ color: '#5b8def', fontWeight: '600', fontSize: 13 }}>← Venue & role</Text>
                </TouchableOpacity>
              ) : null}
              <Text style={{ color: '#fff', fontSize: 20, fontWeight: '700' }}>Grade</Text>
              <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }} numberOfLines={1}>
                {showingForm
                  ? `${venue?.name || 'Venue'} · ${ROLES.find((r) => r.value === role)?.label || role}`
                  : 'Search a venue, pick your role, then open the questionnaire.'}
              </Text>
            </View>
            <TouchableOpacity
              onPress={resetAndClose}
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                backgroundColor: 'rgba(255,255,255,0.1)',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text style={{ color: '#fff', fontSize: 18, fontWeight: '700' }}>✕</Text>
            </TouchableOpacity>
          </View>

          {showingForm ? (
            <View style={{ flex: 1, minHeight: 0, backgroundColor: '#e9e9e9' }}>
              <VenueReportForm
                venue={venue}
                initialUserType={role}
                lockUserType
                embedded
                onSubmit={resetAndClose}
                onCancel={() => setStep('pick')}
              />
            </View>
          ) : (
            <View style={{ flex: 1, padding: 18 }}>
              <Text style={{ color: '#5b8def', fontSize: 12, fontWeight: '700', letterSpacing: 0.6, marginBottom: 6 }}>
                VENUE
              </Text>
              <TextInput
                value={q}
                onChangeText={(value) => {
                  setQ(value);
                  setVenue(null);
                }}
                placeholder="Search venues by name, city, or state…"
                placeholderTextColor="rgba(255,255,255,0.35)"
                autoCorrect={false}
                autoCapitalize="none"
                returnKeyType="search"
                style={{
                  backgroundColor: '#1c1c1c',
                  color: '#fff',
                  borderRadius: 10,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  marginBottom: 8,
                }}
              />
              {loading && catalog.length === 0 ? (
                <ActivityIndicator color="#5b8def" style={{ marginVertical: 12 }} />
              ) : (
                <ScrollView style={{ flexGrow: 0, maxHeight: 280, marginBottom: 14 }}>
                  {showEmptyHint ? (
                    <Text style={{ color: 'rgba(255,255,255,0.4)', paddingVertical: 10, paddingHorizontal: 8 }}>
                      Type a venue name, city, or state — same search as Venue Database.
                    </Text>
                  ) : null}
                  {showNoHits ? (
                    <Text style={{ color: 'rgba(255,255,255,0.4)', paddingVertical: 10, paddingHorizontal: 8 }}>
                      No venues match that search.
                    </Text>
                  ) : null}
                  {matches.map((row) => {
                    const selected = venue?.id === row.id;
                    const detail = formatVenueSearchResultMeta(row).detailLine;
                    return (
                      <TouchableOpacity
                        key={row.id}
                        onPress={() => {
                          setVenue(row);
                          setQ(row.name || q);
                        }}
                        style={{
                          paddingVertical: 9,
                          paddingHorizontal: 8,
                          borderRadius: 8,
                          backgroundColor: selected ? 'rgba(91,141,239,0.18)' : 'transparent',
                        }}
                      >
                        <Text style={{ color: '#fff', fontWeight: selected ? '700' : '500' }}>{row.name}</Text>
                        {detail ? (
                          <Text style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>{detail}</Text>
                        ) : null}
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              )}

              <Text style={{ color: '#5b8def', fontSize: 12, fontWeight: '700', letterSpacing: 0.6, marginBottom: 8 }}>
                YOUR ROLE
              </Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                {ROLES.map((r) => {
                  const selected = role === r.value;
                  return (
                    <TouchableOpacity
                      key={r.value}
                      onPress={() => setRole(r.value)}
                      style={{
                        paddingHorizontal: 12,
                        paddingVertical: 8,
                        borderRadius: 16,
                        backgroundColor: selected ? '#5b8def' : '#1c1c1c',
                      }}
                    >
                      <Text style={{ color: '#fff', fontWeight: '600', fontSize: 13 }}>{r.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>

              <TouchableOpacity
                disabled={!canContinue}
                onPress={() => {
                  if (!canContinue) return;
                  setStep('form');
                }}
                style={{
                  backgroundColor: canContinue ? '#5b8def' : '#2a2a2a',
                  borderRadius: 10,
                  paddingVertical: 12,
                  alignItems: 'center',
                }}
              >
                <Text style={{ color: canContinue ? '#fff' : 'rgba(255,255,255,0.35)', fontWeight: '700' }}>
                  Open questionnaire
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
}
